import { computed, onMounted, watch } from 'vue'

export interface SEOConfig {
  title: string
  description: string
  keywords: string
  ogImage?: string
  canonical?: string
  noindex?: boolean
  structuredData?: any
}

export interface ProductMeta {
  id: string
  name: string
  description?: string
  price: number
  currency: string
  availability: 'InStock' | 'OutOfStock'
  category: string
  image?: string
  sku?: string
}

const pageConfigs = {
  home: {
    title: 'Retailer Management System - Fresh Produce POS & Inventory',
    description: 'Comprehensive retail management platform for Kenyan produce vendors. Manage inventory, process sales, accept M-Pesa payments, and track orders with real-time analytics.',
    keywords: 'retail management, inventory tracking, point of sale, m-pesa integration, fresh produce, kenyan market, stock management, pos system'
  },
  catalog: {
    title: 'Product Catalog - Fresh Produce & Inventory | Retailer Management',
    description: 'Browse our fresh produce catalog with real-time stock levels, prices in KES, and easy ordering. Quality fruits, vegetables, and produce for Kenyan customers.',
    keywords: 'fresh produce, product catalog, kenyan fruits, vegetables, stock levels, prices kes, online ordering, fresh food'
  },
  login: {
    title: 'Login - Retailer Management System',
    description: 'Secure login for vendors and staff to access the retail management system. Manage your inventory, sales, and orders with M-Pesa integration.',
    keywords: 'login, vendor access, retail management, secure authentication, pos access'
  },
  register: {
    title: 'Register - Join Retailer Management System',
    description: 'Register your produce business with our comprehensive retail management platform. Start managing inventory, processing sales, and accepting M-Pesa payments today.',
    keywords: 'register, sign up, vendor registration, retail business, pos system setup, m-pesa integration'
  },
  about: {
    title: 'About - Retailer Management System for Fresh Produce',
    description: 'Learn about our comprehensive retail management platform designed specifically for Kenyan produce vendors. Features include inventory tracking, M-Pesa integration, and sales analytics.',
    keywords: 'about, retail management system, kenyan produce vendors, inventory software, m-pesa pos, business management'
  },
  dashboard: {
    title: 'Admin Dashboard - Retailer Management System',
    description: 'Comprehensive admin dashboard for managing your retail business. View sales analytics, inventory alerts, and business metrics in real-time.',
    keywords: 'admin dashboard, sales analytics, business metrics, inventory alerts, retail management',
    noindex: true
  },
  pos: {
    title: 'Point of Sale - Retailer Management System',
    description: 'Modern POS system for processing walk-in and delivery orders with M-Pesa integration and real-time inventory checking.',
    keywords: 'point of sale, pos system, m-pesa payments, order processing, inventory checking',
    noindex: true
  },
  inventory: {
    title: 'Inventory Management - Retailer Management System',
    description: 'Comprehensive inventory management with stock tracking, expiry date monitoring, and automated low-stock alerts for fresh produce.',
    keywords: 'inventory management, stock tracking, expiry dates, low stock alerts, produce management',
    noindex: true
  },
  products: {
    title: 'Product Management - Retailer Management System',
    description: 'Manage your product catalog with pricing, categories, and stock levels. Add new products and update existing inventory.',
    keywords: 'product management, catalog management, pricing, categories, stock levels',
    noindex: true
  },
  categories: {
    title: 'Category Management - Retailer Management System',
    description: 'Organize your products into categories for better inventory management and customer browsing experience.',
    keywords: 'category management, product organization, inventory categories',
    noindex: true
  },
  orders: {
    title: 'Order Management - Retailer Management System',
    description: 'View and manage customer orders, track delivery status, and process payments with integrated M-Pesa support.',
    keywords: 'order management, customer orders, delivery tracking, payment processing',
    noindex: true
  },
  reports: {
    title: 'Sales Reports & Analytics - Retailer Management System',
    description: 'Comprehensive sales reporting with analytics, trends, and exportable data for business insights and planning.',
    keywords: 'sales reports, analytics, business insights, trends, data export',
    noindex: true
  },
  settings: {
    title: 'System Settings - Retailer Management System',
    description: 'Configure your retail management system settings, user permissions, and business preferences.',
    keywords: 'system settings, configuration, user permissions, business settings',
    noindex: true
  }
}

export function useSEO(routeName?: string) {
  const getPageConfig = (name?: string): SEOConfig => {
    const homeConfig = pageConfigs.home
    if (!homeConfig) throw new Error('Home SEO config is missing')
    
    if (!name) return homeConfig
    const config = pageConfigs[name as keyof typeof pageConfigs]
    return config || homeConfig
  }

  const config = computed(() => getPageConfig(routeName))

  const updateMetaTag = (name: string, content: string, attribute: 'name' | 'property' = 'name') => {
    let meta = document.querySelector(`meta[${attribute}="${name}"]`)
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute(attribute, name)
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', content)
  }

  const updateLinkTag = (rel: string, href: string) => {
    let link = document.querySelector(`link[rel="${rel}"]`)
    if (!link) {
      link = document.createElement('link')
      link.setAttribute('rel', rel)
      document.head.appendChild(link)
    }
    link.setAttribute('href', href)
  }

  const updateSEO = (customConfig?: Partial<SEOConfig>) => {
    const finalConfig = { ...config.value, ...customConfig }
    
    // Update title
    document.title = finalConfig.title
    
    // Update meta tags
    updateMetaTag('description', finalConfig.description)
    updateMetaTag('keywords', finalConfig.keywords)
    updateMetaTag('robots', finalConfig.noindex ? 'noindex, nofollow' : 'index, follow')
    
    // Open Graph
    updateMetaTag('og:title', finalConfig.title, 'property')
    updateMetaTag('og:description', finalConfig.description, 'property')
    updateMetaTag('og:url', finalConfig.canonical || window.location.href, 'property')
    updateMetaTag('og:image', finalConfig.ogImage || 'https://retailer-management-system.com/og-image.jpg', 'property')
    
    // Twitter
    updateMetaTag('twitter:title', finalConfig.title, 'property')
    updateMetaTag('twitter:description', finalConfig.description, 'property')
    updateMetaTag('twitter:image', finalConfig.ogImage || 'https://retailer-management-system.com/twitter-image.jpg', 'property')
    
    // Canonical URL
    if (finalConfig.canonical) {
      updateLinkTag('canonical', finalConfig.canonical)
    }
  }

  const updateProductSEO = (product: ProductMeta) => {
    const productConfig: SEOConfig = {
      title: `${product.name} - Fresh ${product.category} | Kijani Pantry`,
      description: `${product.description || `Buy fresh ${product.name} from Kijani Pantry.`} Available for ${product.currency} ${product.price}. ${product.availability === 'InStock' ? 'In stock and ready for delivery' : 'Currently out of stock'}.`,
      keywords: `${product.name.toLowerCase()}, fresh ${product.category.toLowerCase()}, ${product.currency.toLowerCase()}, kenyan produce, fresh food, ${product.sku || ''}`,
      ogImage: product.image || 'https://retailer-management-system.com/og-image.jpg',
      canonical: `https://retailer-management-system.com/products/${product.id}`,
      structuredData: {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.name,
        "description": product.description || `Fresh ${product.name} - ${product.category}`,
        "sku": product.sku || product.id,
        "category": product.category,
        "image": product.image ? [product.image] : undefined,
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": product.currency,
          "availability": `https://schema.org/${product.availability}`,
          "seller": {
            "@type": "Organization",
            "name": "Kijani Pantry"
          }
        }
      }
    }
    
    updateSEO(productConfig)
  }

  const updateCategorySEO = (categoryName: string, productCount: number) => {
    const categoryConfig: SEOConfig = {
      title: `${categoryName} - Fresh Produce Category | Kijani Pantry`,
      description: `Browse our ${categoryName.toLowerCase()} category with ${productCount} fresh products. Quality ${categoryName.toLowerCase()} with competitive prices in KES and fast delivery in Kenya.`,
      keywords: `${categoryName.toLowerCase()}, fresh produce, category, kenyan ${categoryName.toLowerCase()}, wholesale ${categoryName.toLowerCase()}, retail ${categoryName.toLowerCase()}`,
      canonical: `https://retailer-management-system.com/catalog?category=${encodeURIComponent(categoryName.toLowerCase())}`
    }
    
    updateSEO(categoryConfig)
  }

  const addStructuredData = (data: any, id: string = 'page-structured-data') => {
    // Remove existing structured data
    const existing = document.getElementById(id)
    if (existing) {
      existing.remove()
    }

    // Add new structured data
    const script = document.createElement('script')
    script.id = id
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(data, null, 2)
    document.head.appendChild(script)
  }

  return {
    config,
    updateSEO,
    updateProductSEO,
    updateCategorySEO,
    addStructuredData,
    pageConfigs
  }
}